import React, { PropsWithChildren } from 'react'
import styled from 'styled-components/native'

import Button, { ButtonProps } from 'APP/Converse/Button'
import Typography from 'APP/Converse/Typography'

import { BottomDrawerProvider } from './BottomDrawerProvider'
import { BottomDrawerContent } from './BottomDrawerContent'
import Header, { BottomDrawerHeaderProps } from './BottomDrawerHeader'

export const Subtitle = styled(Typography)`
  margin-bottom: ${({ theme }) => theme.metrics.baseMargin}px;
`

export const Body = styled.View`
  align-items: flex-start;
  flex-direction: row;
  gap: ${({ theme }) => theme.metrics.baseMargin / 1.5}px;
  justify-content: center;
  width: 100%;
  padding-bottom: ${({ theme }) => theme.insets.bottom}px;
`

export const Title = styled(Typography)`
  color: ${({ theme }) => theme.colors.text};
`

export const ContentContainer = styled(BottomDrawerContent)`
  padding: ${({ theme }) => theme.metrics.baseMargin * 1.5}px;
  justify-content: center;
  flex-direction: row;
`

export const Content = styled.View`
  max-width: 600px;
  width: 100%;
`

export const Overlay = styled.Pressable`
  flex: 1;
  background-color: rgba(0, 0, 0, 0.5);
  justify-content: flex-end;
`

export type BottomDrawerProps = {
  onClose: () => void
  subtitle?: string
  isVisible: boolean
  primaryButtonProps?: ButtonProps
  secondaryButtonProps?: ButtonProps
  testID?: string
} & PropsWithChildren &
  BottomDrawerHeaderProps

/**
 *
 * A modal bottom drawer that can display a title, subtitle, action buttons, and custom content.
 * It is useful for presenting users with a choice or additional information that requires their attention.
 *
 * @param {BottomDrawerProps} props - The props for the BottomDrawer component.
 * @returns {JSX.Element} The rendered BottomDrawer component.

 */

function BottomDrawer({
  title,
  subtitle,
  isVisible,
  onClose,
  primaryButtonProps,
  secondaryButtonProps,
  testID = 'BOTTOM_DRAWER',
  children,
  closeButtonProps,
}: BottomDrawerProps) {
  return (
    <BottomDrawerProvider isVisible={isVisible} onClose={onClose} testID={testID}>
      <Overlay
        onPress={onClose}
        accessibilityRole="button"
        accessibilityLabel="Close drawer"
        testID="BOTTOM_DRAWER_OVERLAY"
      >
        <ContentContainer>
          <Content>
            {title && <Header title={title} closeButtonProps={closeButtonProps} />}
            {subtitle && <Subtitle variant="bodyNormal">{subtitle}</Subtitle>}
            {children || (
              <Body>
                {secondaryButtonProps && (
                  <Button
                    variant="secondary"
                    widthVariant="flex"
                    textColor="error"
                    small
                    {...secondaryButtonProps}
                  />
                )}
                {primaryButtonProps && (
                  <Button variant="primary" widthVariant="flex" small {...primaryButtonProps} />
                )}
              </Body>
            )}
          </Content>
        </ContentContainer>
      </Overlay>
    </BottomDrawerProvider>
  )
}

export default BottomDrawer
